/**
 * Toast flotante global de jaulas vencidas. Aparece arriba al entrar/recargar
 * la app (en cualquier pantalla), solo para admin y recepción. "Ver ahora"
 * lleva a la pantalla de Jaulas.
 */
import { useState } from 'react';
import { Pressable, Text, View, useWindowDimensions } from 'react-native';
import Animated, { SlideInRight, FadeOut } from 'react-native-reanimated';
import { Container, X } from 'lucide-react-native';

import { colors, typography } from '@/theme/tokens';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { jaulasQuery, type Jaula } from '@/services/firebase/jaulas';
import { useAuth, selectIsAdmin, selectIsRecepcion } from '@/stores/authStore';

const fechaISO = (ts: any): string | null => {
  const d = ts?.toDate?.();
  if (!d) return null;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};
const hoyISO = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const estaVencida = (j: Jaula): boolean => {
  if (j.estado !== 'prestada') return false;
  const h = fechaISO(j.prestadaHasta);
  return !!h && h < hoyISO();
};

export function JaulasVencidasToast({ onVer }: { onVer: () => void }) {
  const { width } = useWindowDimensions();
  const wide = width >= 920;
  const isAdmin = useAuth(selectIsAdmin);
  const isRecepcion = useAuth(selectIsRecepcion);
  const puedeVer = isAdmin || isRecepcion;

  const [cerrado, setCerrado] = useState(false);
  const { data: jaulas } = useFirestoreCollection<Jaula>(puedeVer ? jaulasQuery : null);

  if (!puedeVer || cerrado) return null;
  const vencidas = jaulas.filter(estaVencida);
  if (vencidas.length === 0) return null;

  const count = vencidas.length;
  const titulo = count === 1 ? `${vencidas[0].nombre} vencida` : `${count} jaulas vencidas`;

  return (
    <View
      pointerEvents="box-none"
      style={{ position: 'absolute', top: 14, left: 12, right: wide ? 18 : 12, alignItems: 'flex-end', zIndex: 60 }}
    >
      <Animated.View
        entering={SlideInRight.duration(340)}
        exiting={FadeOut.duration(160)}
        style={{
          maxWidth: wide ? 360 : 300,
          width: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 13,
          paddingVertical: 15,
          paddingLeft: 15,
          paddingRight: 40,
          borderRadius: 20,
          backgroundColor: colors.magenta[700],
          // @ts-ignore web only (sombra)
          boxShadow: '0 14px 34px -10px rgba(59,8,32,0.6)',
        }}
      >
        <View style={{ width: 44, height: 44, borderRadius: 14, backgroundColor: 'rgba(255,255,255,0.16)', alignItems: 'center', justifyContent: 'center' }}>
          <Container size={22} color={colors.white} />
        </View>
        <View style={{ flex: 1, gap: 9 }}>
          <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 15 }} numberOfLines={2}>
            {titulo}
          </Text>
          <Pressable
            onPress={() => { onVer(); setCerrado(true); }}
            style={({ hovered }) => ({
              alignSelf: 'flex-start',
              backgroundColor: hovered ? 'rgba(0,0,0,0.34)' : 'rgba(0,0,0,0.24)',
              paddingHorizontal: 15,
              paddingVertical: 8,
              borderRadius: 999,
              // @ts-ignore web only
              cursor: 'pointer',
            })}
          >
            <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 13 }}>Ver ahora →</Text>
          </Pressable>
        </View>
        <Pressable
          onPress={() => setCerrado(true)}
          hitSlop={8}
          style={{ position: 'absolute', top: 12, right: 12, width: 26, height: 26, alignItems: 'center', justifyContent: 'center', borderRadius: 8 }}
        >
          <X size={16} color={colors.white} />
        </Pressable>
      </Animated.View>
    </View>
  );
}
